import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl, FormLabel,
    HStack,
    Icon,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    Textarea,
    useToast
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FiPlusCircle } from "react-icons/fi";
import Select from "react-select";
import { fetchAllCouriers, sendMessage } from "../../api";
import { getSelectStyles, useTableColors } from "../../Utils";

const SendMessageModal = ({ isOpen, onClose }) => {
    const [couriers, setCouriers] = useState([]);
    const courierOptions = couriers.map(c => ({ value: c._id, label: c.name }));
    const coolors = useTableColors()
    const [formData, setFormData] = useState({
        courierIds: [],
        message: "",
        images: [],
        priority: "normal",
        toAll: false,
        notification: false,
    });

    const [loading, setLoading] = useState(false);
    const toast = useToast();

    useEffect(() => {
        fetchCouriers();
    }, []);

    const fetchCouriers = async () => {
        try {
            const response = await fetchAllCouriers(1, 999);
            if (response.success) {
                setCouriers(response.data.couriers);
            }
        } catch (error) {
            toast({ title: "Error loading couriers", status: "error" });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "courierIds") {
            const options = [...e.target.selectedOptions];
            setFormData({ ...formData, courierIds: options.map(opt => opt.value) });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleCourierSelectChange = (selectedOptions) => {
        setFormData({
            ...formData,
            courierIds: selectedOptions ? selectedOptions.map(opt => opt.value) : []
        });
    };


    const handleCheckboxChange = (e) => {
        setFormData({ ...formData, toAll: e.target.checked, courierId: e.target.checked ? "" : formData.courierId });
    };


    const handleSubmit = async () => {
        if (!formData.toAll && (!formData.courierIds || formData.courierIds.length === 0)) {
            toast({ title: "Please select at least one courier", status: "warning" });
            return;
        }
        if (!formData.message.trim()) {
            toast({ title: "Message is required", status: "warning" });
            return;
        }

        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("message", formData.message || "");
            fd.append("priority", formData.priority || "normal");
            fd.append("toAll", formData.toAll);
            fd.append("notification", formData.notification);

            if (!formData.toAll && formData.courierIds.length > 0) {
                formData.courierIds.forEach((id) => fd.append("courierIds", id));
            }

            formData.images.forEach((img) => fd.append("documents", img));

            await sendMessage(fd);

            setFormData({
                courierIds: [],
                message: "",
                images: [],
                priority: "normal",
                toAll: false,
                notification: false,
            });
            toast({ title: "Message sent!", status: "success" });
            onClose();
        } catch (error) {
            toast({ title: "Failed to send message", status: "error" });
        }
        setLoading(false);
    };


    return (
        <Modal size={"3xl"} isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Send Message</ModalHeader>
                <ModalBody>

                    <HStack mb={4}>
                        <FormControl mb={3}>
                            <Checkbox isChecked={formData.toAll} onChange={handleCheckboxChange}>
                                Send to All Couriers
                            </Checkbox>
                        </FormControl>

                        <FormControl mb={3}>
                            <Checkbox
                                isChecked={formData.notification}
                                onChange={(e) => setFormData({ ...formData, notification: e.target.checked })}
                            >
                                Send as Push Notification
                            </Checkbox>
                        </FormControl>
                    </HStack>

                    <Divider />
                    <FormControl mt={4} isRequired isDisabled={formData.toAll}>
                        <FormLabel>Couriers</FormLabel>
                        <Select
                            isMulti
                            options={courierOptions}
                            onChange={handleCourierSelectChange}
                            value={courierOptions.filter(opt => formData.courierIds.includes(opt.value))}
                            isDisabled={formData.toAll}
                            placeholder="Select Couriers"
                            styles={getSelectStyles(coolors)}
                            classNamePrefix="rs"
                        />
                    </FormControl>




                    <FormControl mt={3} isRequired>
                        <FormLabel>Message</FormLabel>
                        <Textarea name="message" onChange={handleChange} placeholder="Type your message..." />
                    </FormControl>


                    <FormControl mt={3}>
                        <FormLabel>Priority</FormLabel>
                        <Select
                            name="priority"
                            options={[
                                { value: "normal", label: "Normal" },
                                { value: "urgent", label: "Urgent" }
                            ]}
                            value={{ value: formData.priority, label: formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1) }}
                            onChange={(selected) => setFormData({ ...formData, priority: selected.value })}
                            styles={getSelectStyles(coolors)}
                            classNamePrefix="rs"
                        />

                    </FormControl>
                    <Text color={'red'} fontSize={"xs"}>Urgent Priority will send critical alert to the couriers, please use it carefully.</Text>


                    <FormControl mt={3}>
                        <FormLabel>Attach Images</FormLabel>
                        <Box
                            border="2px dashed"
                            borderColor="gray.300"
                            borderRadius="md"
                            p={6}
                            h="240px"
                            display="flex"
                            flexDir="column"
                            alignItems="center"
                            justifyContent="center"
                            textAlign="center"
                            cursor="pointer"
                            // bg="gray.50"
                            // _hover={{ bg: "gray.100" }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
                                if (files.length) {
                                    setFormData({ ...formData, images: [...formData.images, ...files] });
                                }
                            }}
                            onClick={() => document.getElementById("fileInput").click()}
                        >
                            {formData.images.length > 0 ? (
                                <HStack wrap="wrap" spacing={3} justify="center">
                                    {formData.images.map((img, i) => (
                                        <Box key={i} position="relative">
                                            <img
                                                src={URL.createObjectURL(img)}
                                                alt="preview"
                                                style={{
                                                    width: "100px",
                                                    height: "100px",
                                                    objectFit: "cover",
                                                    borderRadius: "8px",
                                                }}
                                            />
                                            <Button
                                                size="xs"
                                                colorScheme="red"
                                                position="absolute"
                                                top="2px"
                                                right="2px"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFormData({
                                                        ...formData,
                                                        images: formData.images.filter((_, idx) => idx !== i),
                                                    });
                                                }}
                                            >
                                                ✕
                                            </Button>
                                        </Box>
                                    ))}
                                </HStack>
                            ) : (
                                <>
                                    <Icon as={FiPlusCircle} boxSize={10} color="gray.500" mb={2} />
                                    <Text mt={2} fontSize="sm" color="gray.600" fontWeight="medium">
                                        Drag & drop or click to upload multiple images
                                    </Text>
                                </>
                            )}
                        </Box>

                        <input
                            id="fileInput"
                            type="file"
                            accept="image/*"
                            multiple
                            hidden
                            onChange={(e) =>
                                setFormData({ ...formData, images: Array.from(e.target.files) })
                            }
                        />
                    </FormControl>





                    {/* <FormControl mt={3}>
                        <FormLabel>Attach Image</FormLabel>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                                setFormData({ ...formData, image: e.target.files[0] })
                            }
                        />
                    </FormControl> */}


                </ModalBody>

                <ModalFooter>
                    <Button onClick={() => {
                        setFormData({
                            courierIds: [],
                            message: "",
                            images: [],
                            priority: "normal",
                            toAll: false,
                            notification: false,
                        });
                        onClose();
                    }} mr={3}>Cancel</Button>
                    <Button colorScheme="blue" onClick={handleSubmit} isLoading={loading}>Send</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SendMessageModal;
